import {
  type InputHTMLAttributes,
  use,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { useExternalUrlValidation } from '../../hooks/useExternalUrlValidation';
import { Input as InputBase } from '../Input';
import { UrlValidationStatus } from '../UrlValidationStatus';
import { FormContext } from './FormContext';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  checkReachability?: boolean;
  explanation?: string;
  label: string;
  name: string;
  reachabilityMode?: 'always' | 'changed';
  validateHttps?: boolean;
}

function getUrlFormatError(value: string, validateHttps: boolean) {
  if (!value.trim()) {
    return '';
  }

  try {
    const url = new URL(value);
    return validateHttps && url.protocol !== 'https:'
      ? 'Use an HTTPS link.'
      : '';
  } catch {
    return 'Enter a full URL, like https://example.com.';
  }
}

export function shouldRunUrlReachabilityCheck(input: {
  checkReachability: boolean;
  formatError: string;
  initialValue: string;
  reachabilityMode: 'always' | 'changed';
  value: string;
}) {
  return (
    input.checkReachability &&
    !input.formatError &&
    (input.reachabilityMode === 'always' ||
      input.value.trim() !== input.initialValue.trim())
  );
}

export function UrlInput(props: Props) {
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { setValid } = use(FormContext);
  const initialValueRef = useRef(props.defaultValue || props.value || '');
  const [value, setValue] = useState(initialValueRef.current);
  const {
    checkReachability = true,
    explanation,
    label,
    className,
    reachabilityMode = 'always',
    validateHttps = true,
    onChange,
    ...rest
  } = props;
  const valueString = typeof value === 'string' ? value : String(value);
  const initialValueString =
    typeof initialValueRef.current === 'string'
      ? initialValueRef.current
      : String(initialValueRef.current);
  const formatError = getUrlFormatError(valueString, validateHttps);
  const reachabilityShouldRun = shouldRunUrlReachabilityCheck({
    checkReachability,
    formatError,
    initialValue: initialValueString,
    reachabilityMode,
    value: valueString,
  });
  const localError = formatError || error;
  const reachability = useExternalUrlValidation(valueString, {
    enabled: reachabilityShouldRun,
  });
  const reachabilityError = reachability.blocking ? reachability.message : '';
  const validationError = localError || reachabilityError;
  const validationMessage =
    validationError ||
    (reachability.status === 'idle' ? '' : reachability.message);

  useEffect(() => {
    setError(formatError);
  }, [formatError, valueString]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    input.setCustomValidity(validationError);
    setValid(input.form?.checkValidity() ?? true);
  }, [setValid, validationError]);

  const applyUrlInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  return (
    <label className={twMerge('block', 'group', className)}>
      <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="text-sm font-bold text-white">{label}</div>
        <UrlValidationStatus
          className="sm:max-w-[55%] sm:justify-end"
          hasLocalError={Boolean(localError)}
          message={validationMessage}
          status={reachability.status}
        />
      </div>
      {explanation && (
        <div className="text-sm text-neutral-400">{explanation}</div>
      )}
      <InputBase
        ref={inputRef}
        type="url"
        wrapperClassName="mt-1"
        onChange={applyUrlInput}
        onInvalid={(e) => {
          setError(e.currentTarget.validationMessage);
        }}
        {...rest}
      />
    </label>
  );
}
